import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod/v4';
import { useIxmUserauthV1InvitationServiceAcceptOrganizationMemberInvitation } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-invitation-service-accept-organization-member-invitation';
import { useIxmUserauthV1InvitationServiceCheckOrganizationMemberInvitation } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-invitation-service-check-organization-member-invitation';
import { useIxmUserauthV1InvitationServiceDeclineOrganizationMemberInvitation } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-invitation-service-decline-organization-member-invitation';
import { buildIxmUserauthV1SessionServiceWhoamiKey } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-session-service-whoami';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { FrameLoadingTrigger } from '@interxfi/design-system/components/frame/context';
import { TextButton } from '@interxfi/design-system/components/link/text-link/text-button';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import { AuthLayout } from '../features/auth/layout';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../util/error-handler';
import { DEFAULT_AUTHENTICATED_ROUTE } from '../util/routing/defaults';
import type { RouteGuardOptions } from '../util/routing/guard';
import { getPageHead } from '../util/routing/meta';

const acceptInvitationSearchSchema = z.object({
  id: z.string().optional(),
  secret: z.string().optional(),
  organizationId: z.string().optional(),
});

export const Route = createFileRoute('/accept-invitation')({
  staticData: {
    guard: { auth: { requireAuthenticated: true, redirectToIfNotMet: '/register' } },
  } as const satisfies RouteGuardOptions,
  validateSearch: acceptInvitationSearchSchema,
  head: () => getPageHead('Join Organization'),
  component: AcceptInvitation,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to join organization', 'join this organization');
const validateTokenErrorMessageGetter = buildDefaultErrorMessageGetter('Failed to validate invitation', 'validate the invitation');

function AcceptInvitation() {
  const queryClient = useQueryClient();
  const addToast = useAddToast();
  const { id: tokenId, secret: tokenSecret } = Route.useSearch();
  const navigate = useNavigate();
  const {
    mutateAsync: acceptInvitation,
    error: acceptError,
    isPending: acceptPending,
  } = useIxmUserauthV1InvitationServiceAcceptOrganizationMemberInvitation({
    onSuccess: async () => {
      try {
        await queryClient.invalidateQueries({ queryKey: buildIxmUserauthV1SessionServiceWhoamiKey() });
      } catch {}
    },
  });
  useErrorHandler(acceptError, errorMessageGetter);
  const {
    data,
    error: checkTokenError,
    isPending: checkTokenIsPending,
  } = useIxmUserauthV1InvitationServiceCheckOrganizationMemberInvitation(
    tokenSecret && tokenId
      ? {
          token: { secret: tokenSecret, id: tokenId },
        }
      : undefined,
  );
  useErrorHandler(checkTokenError, validateTokenErrorMessageGetter);

  const handleAcceptInvitation = async () => {
    if (!tokenSecret || !tokenId) {
      return;
    }

    try {
      const result = await acceptInvitation({
        token: { secret: tokenSecret, id: tokenId },
      });

      if (result) {
        addToast({
          title: 'Invitation accepted',
          content: `You have joined ${data?.invitation?.data?.organization?.name || 'your organization'}`,
          variant: 'success',
        });

        await navigate({ to: '/user' });
      }
    } catch {}
  };

  const {
    mutateAsync: declineInvitation,
    error: declineError,
    isPending: declinePending,
  } = useIxmUserauthV1InvitationServiceDeclineOrganizationMemberInvitation();
  useErrorHandler(declineError);

  const handleDeclineInvitation = async () => {
    if (!tokenSecret || !tokenId) {
      return;
    }

    try {
      const result = await declineInvitation({
        token: { secret: tokenSecret, id: tokenId },
      });

      if (result) {
        addToast({
          title: 'Invitation declined',
          content: `You have declined the invitation to join ${data?.invitation?.data?.organization?.name || 'this organization'}`,
          variant: 'success',
        });

        await navigate({ to: DEFAULT_AUTHENTICATED_ROUTE });
      }
    } catch {}
  };

  const [acceptButtonState, acceptButtonRef] = useButtonState(acceptPending, acceptError);
  const [declineButtonState, declineButtonRef] = useButtonState(declinePending, declineError);

  const hasToken = Boolean(tokenId && tokenSecret);

  useEffect(() => {
    if (!hasToken) {
      addToast({
        id: 'organizationInvitationTokenMissing',
        content:
          'We could not find your invitation. Please verify that you followed the link in your email, or contact your organization administrator for another invitation.',
        variant: 'error',
      });
    }
  }, [addToast, hasToken]);

  const isPending = acceptPending || declinePending;

  if (hasToken && !data) {
    return (
      <AuthLayout>
        <VerticalStack align="center" gap={7} inlineAlign="stretch">
          <Typography as="h2" lineHeight="1.5rem" size="1rem">
            {checkTokenIsPending ? <Skeleton /> : 'Invitation Not Found'}
          </Typography>

          {checkTokenError ? (
            <Typography lineHeight="1.125rem" size="0.875rem">
              Your invitation is invalid. Please ask your organization administrator to send another invitation.
            </Typography>
          ) : (
            <VerticalStack gap={4}>
              <Skeleton />
              <Skeleton />
            </VerticalStack>
          )}

          {!checkTokenIsPending ? (
            <VerticalStack inlineAlign="center">
              <TextLink LinkElement={Link} size="medium" to={DEFAULT_AUTHENTICATED_ROUTE} underline="hover">
                Return to Portal
              </TextLink>
            </VerticalStack>
          ) : null}
        </VerticalStack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {hasToken && !data ? (
        <FrameLoadingTrigger />
      ) : (
        <VerticalStack align="center" gap={7} inlineAlign="stretch">
          <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading" weight="bold">
            Join Organization
          </Typography>

          <Typography lineHeight="1.125rem" size="0.875rem">
            You've been invited to join <Typography weight="bold">{data?.invitation?.data?.organization?.name ?? 'an organization'}</Typography> on
            Interchange.
          </Typography>

          <Button
            disabled={!hasToken || isPending}
            fullWidth
            onClick={handleAcceptInvitation}
            ref={acceptButtonRef}
            size="large"
            state={acceptButtonState}
          >
            Accept Invitation
          </Button>

          <VerticalStack inlineAlign="center">
            <TextButton disabled={!hasToken || isPending} onClick={handleDeclineInvitation} ref={declineButtonRef} size="medium" underline="hover">
              Decline Invitation
            </TextButton>
          </VerticalStack>
        </VerticalStack>
      )}
    </AuthLayout>
  );
}
